import { type ElementType, type ReactNode, type Ref } from "react";
import { Avatar as MuiAvatar, type SxProps, type Theme } from "@mui/material";

import { mergeSx } from "ui/merge-sx";

const SIZE_REM = {
  "2xs": "1rem",
  xs: "1.5rem",
  sm: "2rem",
  md: "3rem",
  lg: "4rem",
  xl: "6rem",
  "2xl": "8rem",
  full: "100%",
} as const;

export type AvatarSize = keyof typeof SIZE_REM;

export type AvatarProps = {
  name?: string;
  src?: string;
  size?: AvatarSize;
  alt?: string;
  component?: ElementType;
  className?: string;
  children?: ReactNode;
  sx?: SxProps<Theme>;
  ref?: Ref<HTMLDivElement>;
} & Record<`data-${string}`, unknown>;

const getInitials = (name: string) => {
  const [firstName, lastName] = name.split(" ");
  if (!firstName) return "";
  return lastName ? `${firstName.charAt(0)}${lastName.charAt(0)}` : firstName.charAt(0);
};

export const Avatar = ({
  component = "div",
  size = "md",
  name,
  src,
  alt,
  className,
  children,
  sx,
  ref,
  ...rest
}: AvatarProps) => {
  const diameter = SIZE_REM[size];
  const initials = name ? getInitials(name) : "";

  return (
    <MuiAvatar
      ref={ref}
      component={component}
      className={className}
      src={src || undefined}
      alt={alt ?? name}
      sx={mergeSx(
        {
          width: diameter,
          height: diameter,
          fontSize: `calc(${diameter} / 2.5)`,
          backgroundColor: "grey.400",
          color: "grey.50",
        },
        sx,
      )}
      {...rest}
    >
      {children ?? (initials || undefined)}
    </MuiAvatar>
  );
};
